// src/components/FichaDetails.jsx

import React, {
  useMemo,
  useState,
  useCallback,
  useEffect
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useFetchData from '../../../../repo/get';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Alert,
  Snackbar,
  Container,
  Grid,
  Divider,
  Stack,
  IconButton,
  Collapse,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  ArrowBack,
  Download,
  Image as ImageIcon,
  ExpandMore,
  ExpandLess,
  PhotoCamera
} from '@mui/icons-material';
import CapturePhotoUploadButton from './CapturePhotoButton';

const formatField = (val) => (val ?? 'Não informado');

const detailFields = [
  { label: 'Data e hora', key: 'horaedia' },
  { label: 'Empresa', key: 'empresa' },
  { label: 'Base', key: 'base' },
  { label: 'Entregador', key: 'entregador' },
  { label: 'Telefone', key: 'entregadortelefone' },
  { label: 'Endereço', key: 'entregadorendereco' },
  { label: 'Motorista', key: 'motorista' },
  { label: 'Telefone', key: 'motoristatelefone' },
  { label: 'Funcionário', key: 'funcionario' },
  { label: 'Local', key: 'local' },
  { label: 'Status', key: 'status' },
  { label: 'Data Final', key: 'dataFinal' },
];

export default function FichaDetails() {
  const history = useHistory();
  const { search } = useLocation();
  const fichaId = new URLSearchParams(search).get('id');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [expandedSection, setExpandedSection] = useState('details');
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const { data: fd, isLoading, error } = useFetchData(
    `api/romaneios/findBySearch/${fichaId}`
  );

  const formatted = useMemo(() => {
    if (!fd) return {};
    return {
      codigodeficha: formatField(fd.codigoUid),
      horaedia: fd.data
        ? dayjs(fd.data).format('DD/MM/YYYY HH:mm')
        : 'Não informado',
      empresa: formatField(fd.empresa?.nome),
      entregador: formatField(fd.entregador?.nome),
      motorista: formatField(fd.motorista?.nome),
      motoristaid: fd.motorista ? formatField(fd.motorista.id) : null,
      funcionario: formatField(fd.funcionario?.nome),
      entregadortelefone: formatField(fd.entregador?.telefone),
      motoristatelefone: formatField(fd.motorista?.telefone),
      entregadorendereco: formatField(fd.entregador?.endereco),
      local: Array.isArray(fd.cidade)
        ? fd.cidade.map((c) => formatField(c.nome)).join(', ')
        : 'Não informado',
      status: formatField(fd.sts),
      dataFinal: fd.dataFinal
        ? dayjs(fd.dataFinal).format('DD/MM/YYYY HH:mm')
        : 'Não informado',
      linkDownload: fd.linkDownload ?? null,
      linkImagem: fd.linkDownloadImg ?? null,
      codigos: Array.isArray(fd.codigos)
        ? fd.codigos.map((c) => c.codigo)
        : []
    };
  }, [fd]);

  useEffect(() => {
    if (formatted.status === 'finalizado') {
      showMessage('Romaneio finalizado com sucesso!', 'success');
      history.replace('/motorista');
    }
    if (formatted.motoristaid != null) {
      const motoristaId = parseInt(formatted.motoristaid, 10);
      const localId = parseInt(localStorage.getItem('id'), 10);
      if (motoristaId !== localId) {
        history.replace('/motorista');
      }
    }
  }, [formatted.status, showMessage, history]);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleImageClick = (url) => {
    setSelectedImage(url);
    setImageDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress size={isMobile ? 40 : 60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || 'Erro ao buscar dados.'}
        </Alert>
        <Button
          variant="contained"
          fullWidth
          startIcon={<ArrowBack />}
          onClick={() => history.goBack()}
        >
          Voltar
        </Button>
      </Container>
    );
  }

  if (!fd) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography align="center" sx={{ mb: 2 }}>
          Nenhum dado encontrado.
        </Typography>
        <Button
          variant="contained"
          fullWidth
          startIcon={<ArrowBack />}
          onClick={() => history.goBack()}
        >
          Voltar
        </Button>
      </Container>
    );
  }

  return (
    <Container
      sx={{
        py: 1,
        pb: 10,
        maxWidth: 'md',
        '-webkit-overflow-scrolling': 'touch',
        overflowX: 'hidden'
      }}
    >
      {/* Botão de voltar flutuante */}
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1000
        }}
      >
        <IconButton
          onClick={() => history.goBack()}
          sx={{
            bgcolor: 'background.paper',
            boxShadow: 2,
            '&:hover': { bgcolor: 'grey.200' }
          }}
        >
          <ArrowBack />
        </IconButton>
      </Box>

      {/* Botão principal de foto */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 16,
          zIndex: 1000
        }}
      >
        <CapturePhotoUploadButton
          id={formatted.codigodeficha}
          variant="contained"
          size="large"
          startIcon={<PhotoCamera />}
          sx={{
            boxShadow: 3,
            borderRadius: '50%',
            height: 56,
            width: 56,
            minWidth: 0,
            padding: 0
          }}
        />
      </Box>

      {/* Título */}
      <Box sx={{ mt: 6, mb: 2 }}>
        <Typography variant="h6" align="center" fontWeight="bold">
          Ficha {formatted.codigodeficha}
        </Typography>
        <Typography variant="body2" color="textSecondary" align="center">
          Detalhes da entrega
        </Typography>
      </Box>

      {/* Seção Informações Gerais */}
      <Paper elevation={1} sx={{ mb: 2, borderRadius: 2 }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8
          }}
          onClick={() => toggleSection('details')}
        >
          <Typography variant="subtitle1">Informações Gerais</Typography>
          {expandedSection === 'details' ? <ExpandLess /> : <ExpandMore />}
        </Box>
        <Collapse in={expandedSection === 'details'}>
          <Grid container spacing={1} sx={{ p: 2 }}>
            {detailFields.map(({ label, key }) => (
              <Grid item xs={12} sm={6} key={key}>
                <Typography variant="caption" color="textSecondary">
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ wordBreak: 'break-word' }}
                >
                  {formatted[key]}
                </Typography>
                <Divider sx={{ my: 1 }} />
              </Grid>
            ))}
          </Grid>
        </Collapse>
      </Paper>

      {/* Seção Arquivos e Imagens */}
      <Paper elevation={1} sx={{ mb: 2, borderRadius: 2 }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8
          }}
          onClick={() => toggleSection('files')}
        >
          <Typography variant="subtitle1">Arquivos e Imagens</Typography>
          {expandedSection === 'files' ? <ExpandLess /> : <ExpandMore />}
        </Box>
        <Collapse in={expandedSection === 'files'}>
          <Box sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<Download />}
                disabled={!formatted.linkDownload}
                onClick={() =>
                  window.open(formatted.linkDownload, '_blank')
                }
              >
                Baixar Arquivo
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<ImageIcon />}
                disabled={!formatted.linkImagem}
                onClick={() => handleImageClick(formatted.linkImagem)}
              >
                Visualizar Imagem
              </Button>
            </Stack>
          </Box>
        </Collapse>
      </Paper>

      {/* Seção Códigos */}
      <Paper elevation={1} sx={{ mb: 2, borderRadius: 2 }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8
          }}
          onClick={() => toggleSection('codes')}
        >
          <Typography variant="subtitle1">
            Códigos ({formatted.codigos.length})
          </Typography>
          {expandedSection === 'codes' ? <ExpandLess /> : <ExpandMore />}
        </Box>
        <Collapse in={expandedSection === 'codes'}>
          <Box sx={{ p: 1 }}>
            {formatted.codigos.length > 0 ? (
              <Grid container spacing={1}>
                {formatted.codigos.map((code, idx) => (
                  <Grid item key={`${code}-${idx}`}>
                    <Typography variant="body2">{code}</Typography>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography
                variant="body2"
                color="textSecondary"
                align="center"
              >
                Nenhum código disponível
              </Typography>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Dialog de imagem */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        fullScreen={isMobile}
        maxWidth="md"
      >
        <DialogTitle>Imagem da Ficha</DialogTitle>
        <DialogContent>
          <img
            src={selectedImage}
            alt="Ficha"
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: 8
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setImageDialogOpen(false)}
            variant="contained"
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificações */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar((prev) => ({ ...prev, open: false }))
        }
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() =>
            setSnackbar((prev) => ({ ...prev, open: false }))
          }
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
